// src/modules/dashboard/components/RecentActivities/index.js
import React, { useState, useCallback } from 'react';
import { Card, ListGroup, Image, Modal } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import PropTypes from 'prop-types';
import { formatDuration } from '../../../../shared/utils/formatters';
import './styles.css';

const getActivityIcon = (activityType) => {
    const iconMap = {
        'running': 'bi-person-walking',
        'cycling': 'bi-bicycle',
        'swimming': 'bi-water',
        'hiking': 'bi-tree',
        'walking': 'bi-person-walking',
        'football': 'bi-trophy',
        'badminton': 'bi-circle',
        'other': 'bi-activity'
    };
    return iconMap[activityType?.toLowerCase()] || 'bi-activity';
};

const getActivityIconColor = (activityType) => {
    const colorMap = {
        'running': 'text-danger',
        'cycling': 'text-primary',
        'swimming': 'text-info',
        'hiking': 'text-success',
        'walking': 'text-secondary',
        'football': 'text-warning',
        'badminton': 'text-primary',
        'other': 'text-muted'
    };
    return colorMap[activityType?.toLowerCase()] || 'text-muted';
};

const CompactActivityItem = ({ activity, onImageClick }) => {
    const activityType = activity.activityType?.charAt(0).toUpperCase() + activity.activityType?.slice(1);
    const hasAttachments = activity.photos?.length > 0 || activity.attachments?.length > 0;

    return (
        <div className="recent-activity-item">
            <Link to={`/activity/${activity.activityId}`} className="recent-activity-link">
                <div className="recent-activity-type">
                    <i className={`bi ${getActivityIcon(activity.activityType)} ${getActivityIconColor(activity.activityType)} me-1`}></i>
                    {activityType}
                </div>
                <div className="recent-activity-stats">
                    <span className="recent-stat">
                        <strong>{parseFloat(activity.distance).toFixed(1)}</strong> km
                    </span>
                    <span className="recent-stat-separator">·</span>
                    <span className="recent-stat">
                        <strong>{formatDuration(activity.duration)}</strong>
                    </span>
                    <span className="recent-stat-separator">·</span>
                    <span className="recent-stat recent-points">
                        <strong>{parseFloat(activity.points).toFixed(1)}</strong> pts
                    </span>
                    {activity.penaltyFactor !== undefined && activity.penaltyFactor !== null && activity.penaltyFactor < 1.0 && (
                        <>
                            <span className="recent-stat-separator">·</span>
                            <span className="recent-stat recent-penalty" title={`Penalty: ${Math.round((1 - activity.penaltyFactor) * 100)}% reduction`}>
                                <i className="bi bi-exclamation-triangle"></i> {activity.penaltyFactor}×
                            </span>
                        </>
                    )}
                </div>
                {activity.description && (
                    <div className="recent-activity-description">
                        {activity.description}
                    </div>
                )}
            </Link>
            {hasAttachments && (
                <div className="recent-activity-attachments">
                    {activity.photos?.slice(0, 3).map((photo, index) => (
                        <div
                            key={`photo-${index}`}
                            className="recent-activity-thumbnail"
                            onClick={(e) => {
                                e.preventDefault();
                                onImageClick?.(photo);
                            }}
                        >
                            <Image src={photo} alt="Activity photo" />
                        </div>
                    ))}
                    {activity.attachments?.slice(0, 3).map((attachment, index) => (
                        <div
                            key={`attachment-${index}`}
                            className="recent-activity-thumbnail"
                            onClick={(e) => {
                                e.preventDefault();
                                onImageClick?.(attachment.fullUrl || attachment.url);
                            }}
                        >
                            <Image src={attachment.url} alt={attachment.name} />
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
};

const RecentActivities = ({
    activities = [],
    title = "Recent Activities",
    viewAllLink = "/activities",
    showAddButton = true,
    maxItems = 5
}) => {
    const [enlargedImage, setEnlargedImage] = useState(null);

    const handleClose = useCallback(() => {
        setEnlargedImage(null);
    }, []);

    const handleImageClick = useCallback((imageUrl) => {
        setEnlargedImage(imageUrl);
    }, []);

    return (
        <>
            <Card className="recent-activities-card">
                <Card.Header className="d-flex justify-content-between align-items-center">
                    <h5 className="mb-0">
                        <i className="bi bi-clock-history me-2 text-info"></i>
                        {title}
                    </h5>
                </Card.Header>
                <ListGroup variant="flush">
                    {activities.length > 0 ? (
                        activities.slice(0, maxItems).map((activity) => (
                            <ListGroup.Item key={activity.activityId} className="p-0">
                                <CompactActivityItem
                                    activity={activity}
                                    onImageClick={handleImageClick}
                                />
                            </ListGroup.Item>
                        ))
                    ) : (
                        <ListGroup.Item className="text-center text-muted py-4">
                            <i className="bi bi-calendar-x d-block mb-2" style={{ fontSize: '1.5rem' }}></i>
                            No recent activities
                        </ListGroup.Item>
                    )}
                </ListGroup>
                <Card.Footer className="text-center">
                    {showAddButton && (
                        <Link to="/activity" className="btn btn-primary btn-sm me-2">
                            <i className="bi bi-plus-circle me-1"></i>
                            Add Activity
                        </Link>
                    )}
                    <Link to={viewAllLink} className="btn btn-outline-secondary btn-sm">
                        See more
                        <i className="bi bi-arrow-right ms-1"></i>
                    </Link>
                </Card.Footer>
            </Card>
            <Modal
                show={!!enlargedImage}
                onHide={handleClose}
                centered
                size="lg"
            >
                <Modal.Body className="p-0">
                    {enlargedImage && (
                        <Image src={enlargedImage} fluid />
                    )}
                </Modal.Body>
            </Modal>
        </>
    );
};

CompactActivityItem.propTypes = {
    activity: PropTypes.shape({
        activityId: PropTypes.string.isRequired,
        activityType: PropTypes.string.isRequired,
        points: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
        distance: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
        duration: PropTypes.number.isRequired,
        description: PropTypes.string,
        photos: PropTypes.arrayOf(PropTypes.string),
        attachments: PropTypes.arrayOf(PropTypes.shape({
            url: PropTypes.string.isRequired,
            name: PropTypes.string.isRequired
        }))
    }).isRequired,
    onImageClick: PropTypes.func
};

RecentActivities.propTypes = {
    activities: PropTypes.arrayOf(PropTypes.shape({
        activityId: PropTypes.string.isRequired,
        activityType: PropTypes.string.isRequired,
        points: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
        distance: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
        duration: PropTypes.number.isRequired,
        description: PropTypes.string,
        photos: PropTypes.arrayOf(PropTypes.string),
        attachments: PropTypes.arrayOf(PropTypes.shape({
            url: PropTypes.string.isRequired,
            name: PropTypes.string.isRequired
        }))
    })),
    title: PropTypes.string,
    viewAllLink: PropTypes.string,
    showAddButton: PropTypes.bool,
    maxItems: PropTypes.number
};

export default RecentActivities;
